<script async setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
    cart: {
        type: Object,
        default: () => ({})
    },
});

const stickyNotice = ref({
    type: "",
    html: "",
});

const stickyVisible = ref(false);
let stickyTimeout = null;

const showStickyNotice = (detail = {}) => {
    if (stickyTimeout) {
        clearTimeout(stickyTimeout);
        stickyTimeout = null;
    }

    stickyNotice.value = {
        type: detail.type || "info",
        html: detail.html || "",
    };

    if (!stickyNotice.value.html) {
        stickyVisible.value = false;
        return;
    }

    stickyVisible.value = true;

    stickyTimeout = setTimeout(() => {
        stickyVisible.value = false;
        stickyTimeout = null;
    }, 4000);
};

const onGlobalNoticeEvent = (event) => {
    if (!event?.detail) return;
    showStickyNotice(event.detail);
};

const visible = ref(false);
const dataCart = ref(props.cart || {});
const notice = ref(null);

watch(() => props.cart, (v) => { dataCart.value = v || {}; }, { deep: true });
watch(visible, (open) => { open ? lockBody() : unlockBody(); });

const lockBody = () => { document.body.style.overflow = "hidden"; };
const unlockBody = () => { document.body.style.overflow = ""; };

const open = () => (visible.value = true);
const close = () => (visible.value = false);

const onOpenEvent = () => open();
const onUpdateEvent = (e) => { if (e?.detail) dataCart.value = e.detail; };
const onNotifyEvent = (event) => {
    if (event?.detail) {
        notice.value = event.detail;
    }
};

onMounted(() => {
    window.addEventListener("kd:cart:open", onOpenEvent);
    window.addEventListener("kd:cart:update", onUpdateEvent);
    window.addEventListener("kd:cart:notify", onNotifyEvent);
    window.addEventListener("kd:notice", onGlobalNoticeEvent);
    if (visible.value) lockBody();
});

onBeforeUnmount(() => {
    window.removeEventListener("kd:cart:open", onOpenEvent);
    window.removeEventListener("kd:cart:update", onUpdateEvent);
    window.removeEventListener("kd:cart:notify", onNotifyEvent);
    window.removeEventListener("kd:notice", onGlobalNoticeEvent);

    unlockBody();
});

</script>

<template>
    <!-- Notice Bar -->
    <transition name="cart-notice">
        <div
            v-if="stickyVisible && stickyNotice.html"
            class="fixed inset-x-0 top-14 z-50 pointer-events-none"
            role="alert"
            aria-live="polite"
        >
            <div class="w-full bg-white border-t border-black shadow-lg">
                <div class="max-w-ts-content mx-auto p-4 rounded-b-md  text-sm flex items-start gap-3 pointer-events-auto">
                    <!-- eslint-disable vue/no-v-html -->
                    <div class="flex-1" v-html="stickyNotice.html" />
                    <!-- eslint-enable -->
                </div>
            </div>
        </div>
    </transition>
    <!-- Overlay -->
    <div 
        v-if="visible" 
        :class="['fixed inset-x-0 bg-black/80 z-40 bottom-0','top-14','notebook:unscrolled:top-25.5','notebook:scrolled-up:top-25.5','notebook:scrolled-down:top-14']" 
        @click.self="close" 
        aria-hidden="true" 
    />
    <!-- Sidebar -->
    <aside 
        :class="[
            'fixed right-0 z-[110] w-67 lg:w-[380px] max-w bg-white text-black shadow-2xl transition-transform duration-300 flex flex-col bottom-0',
            'top-14',
            'notebook:unscrolled:top-25.5',
            'notebook:scrolled-up:top-25.5',
            'notebook:scrolled-down:top-14',
            visible ? 'translate-x-0' : 'translate-x-full'
        ]"
        role="dialog" 
        aria-label="Warenkorb" 
        :aria-hidden="!visible"
    >
        <!-- Notice -->
        <div v-if="notice?.text" class="flex items-start gap-2 pt-3 px-4">
            <kd-icon-checkmark size="20" fill="black" />
            <div class="text-sm">
                {{ notice.text }}
            </div>
        </div>

        <!-- Inhalt -->
        <div class="overflow-y-auto flex-1 p-4 space-y-4">
            <TransitionGroup name="cart-fade" tag="div" class="space-y-4">
                <template v-if="Array.isArray(dataCart.items) && dataCart.items.length">
                    <div v-for="item in dataCart.items" :key="item.key || item.product_id || item.permalink" class="flex items-start gap-3">
                        <!-- Produktbild -->
                        <a
                            :href="item.product.url"
                            :title="item.product.title"
                            class="block shrink-0"
                        >
                            <img
                                :src="item.product.image?.thumb || item.product.image?.url"
                                :alt="item.product.image?.alt || item.product.title || ''"
                                class="size-25 object-cover rounded-md"
                            >
                        </a>

                        <div class="flex flex-col justify-between gap-1">
                            <div v-if="item.product.brand?.logo?.url">
                                <img 
                                    :src="item.product.brand.logo.url" 
                                    :alt="item.product.brand.logo.alt || item.product.brand.name"
                                    :srcset="item.product.brand.logo.srcset" 
                                    class="w-14 h-auto object-contain"
                                >
                            </div>
                            <!-- Produktname -->
                            <a :href="item.product.url" class="block text-sm">
                                {{ item.product.title }}
                            </a>

                            <!-- Preise -->
                            <div class="flex items-center gap-2 text-sm">
                                <template v-if="item.product.price?.saleText && item.product.price?.saleText !== item.product.price?.regularText">
                                    <span class="line-through text-black/60 decoration-black decoration-2 content-center">
                                        {{ item.product.price.regularText }}
                                    </span>
                                    <span class="font-semibold text-black">
                                        {{ item.product.price.saleText }}
                                    </span>
                                </template>
                                <template v-else>
                                    <span class="font-semibold text-black">
                                        {{ item.product.price?.regularText || item.product.price?.saleText }}
                                    </span>
                                </template> 
                            </div>
                            <div
                                v-if="item.selectedColor"
                                class="flex items-center gap-2"
                            >
                                <div
                                    class="p-0.5 rounded-full border border-black flex items-center justify-center"
                                    :title="item.selectedColor.name"
                                >
                                    <span
                                        class="size-3 rounded-full block"
                                        :style="{
                                            background: item.selectedColor.hex2
                                                ? `linear-gradient(135deg, ${item.selectedColor.hex} 50%, ${item.selectedColor.hex2} 50%)`
                                                : item.selectedColor.hex
                                        }"
                                    ></span>
                                </div>
                                <div class="border-l border-ts-black h-4"></div>
                                <div class="text-sm">x{{ item.qty }}</div>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <p class="text-sm text-gray-500">Keine Produkte im Warenkorb.</p>
                </template>
            </TransitionGroup>
        
        

            <!-- Upsells -->
            <TransitionGroup name="upsell-fade" tag="div">
                <div v-if="Array.isArray(dataCart.upsells) && dataCart.upsells.length" class="pb-4 space-y-3 border-t border-black">
                    <p class="text-md font-semibold pt-4 uppercase">
                        {{ dataCart.upsells_label }}
                    </p>
                    <div
                        v-for="product in dataCart.upsells"
                        :key="product.id || product.url"
                        class="flex items-start gap-3"
                    >
                        <!-- Produktbild + Rabatt-Badge -->
                        <a
                            class="block relative size-25 shrink-0"
                            :href="product.url"
                            :title="product.title"
                        >
                            <img
                                :src="product.image?.thumb || product.image?.url"
                                :alt="product.image?.alt || product.title"
                                class="size-full object-cover rounded-md"
                            >
                
                            <!-- Discount-Badge -->
                            <span
                                v-if="product.badge?.text"
                                class="absolute top-[70%] bg-black text-white text-xs rounded-r-md px-3 py-1"
                            >
                                {{ product.badge.text }}
                            </span>
                        </a>
                    
                        <div class="flex flex-col justify-between gap-1">
                            <!-- Brand-Logo (falls vorhanden und Struktur wie im Helper) -->
                            <div v-if="product.brand?.logo?.url">
                                <img
                                    :src="product.brand.logo.url"
                                    :alt="product.brand.logo.alt || product.brand.name"
                                    :srcset="product.brand.logo.srcset"
                                    class="w-14 h-auto object-contain"
                                >
                            </div>
                        
                            <!-- Produktname -->
                            <a
                                :href="product.url"
                                class="block text-sm"
                            >
                                {{ product.title }}
                            </a>
                        
                            <!-- Preise (gleiches Pattern wie oben) -->
                            <div class="flex items-center gap-2 text-sm">
                                <template
                                    v-if="product.price?.saleText
                                        && product.price.saleText !== product.price.regularText"
                                >
                                    <span class="line-through text-black/60 decoration-black decoration-2">
                                        {{ product.price.regularText }}
                                    </span>
                                    <span class="font-semibold text-black">
                                        {{ product.price.saleText }}
                                    </span>
                                </template>
                                <template v-else>
                                    <span class="font-semibold text-black">
                                        {{ product.price?.regularText || product.price?.saleText }}
                                    </span>
                                </template>
                            </div>
                            <form method="post" class="js-add-to-cart-form flex items-center" @submit.prevent>
                                <input type="hidden" name="add-to-cart" :value="product.id">
                                <button 
                                    type="submit" 
                                    class="transition-transform duration-200 hover:-translate-y-0.5"
                                    :title="`add-to-cart ${product.title}`" 
                                    aria-label="add-to-cart"
                                >
                                    <kd-icon 
                                        icon="cart-plus" 
                                        fill="current" 
                                        size="38"
                                        class="hover:text-white hover:drop-shadow-ts-tight-black" 
                                    />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </TransitionGroup>
        </div>

        <!-- Footer -->
        <div class=" w-full py-4 px-2 lg:px-4 border-t border-black bg-white space-y-3">
            <div class="flex justify-between text-sm font-semibold">
                <span>{{ dataCart.total_label }}</span>
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="dataCart.total"></span>
                <!-- eslint-enable -->
            </div>

            <div class="flex flex-col lg:flex-row gap-2">
                <kd-button variant="white" class="w-full" @click="close">
                    {{ dataCart.button_label_2 }}
                </kd-button>
                <kd-button 
                    :title="dataCart.button_label" 
                    :href="dataCart.url" 
                    variant="black" 
                    class="w-full"
                    icon="cart"
                >
                    {{ dataCart.button_label }}
                </kd-button>
            </div>
        </div>
    </aside>
</template>

<style scoped>
.cart-fade-enter-active,
.cart-fade-leave-active,
.upsell-fade-enter-active,
.upsell-fade-leave-active {
    transition:
        opacity 0.15s cubic-bezier(0.25, 0.1, 0.25, 1),
        transform 0.15s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.cart-fade-enter-from,
.upsell-fade-enter-from {
    opacity: 0;
    transform: translateY(12px);
}

.cart-fade-leave-to,
.upsell-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}


.cart-notice-enter-active,
.cart-notice-leave-active {
    transition:
        transform 0.15s ease-out,
        opacity 0.15s ease-out;
}

.cart-notice-enter-from,
.cart-notice-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}
</style>